import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import {
  CheckCircle, XCircle, UserPlus, FileArrowUp, Trash, ArrowsClockwise,
  ShieldCheck, Clock, Storefront, DownloadSimple, MapPin,
} from "@phosphor-icons/react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_OPTIONS = [
  { id: "all", label: "All" },
  { id: "pending_admin", label: "Pending Admin" },
  { id: "admin_approved", label: "Admin Approved" },
  { id: "pending_validator", label: "Pending Validator" },
  { id: "listed", label: "Listed" },
  { id: "sold", label: "Sold" },
  { id: "rejected", label: "Rejected" },
  { id: "legacy", label: "Legacy" },
];

const STAGE_LABEL = {
  pending_admin: "Pending Admin",
  admin_approved: "Admin Approved",
  pending_validator: "Pending Validator",
  validator_approved: "Validator Approved",
  listed: "Listed",
  sold: "Sold",
  rejected: "Rejected",
  legacy: "Legacy",
};

const STATUS_COLOR = {
  pending_admin: "bg-amber-600/20 text-amber-400 border-amber-600/30",
  admin_approved: "bg-sky-600/20 text-sky-400 border-sky-600/30",
  pending_validator: "bg-violet-600/20 text-violet-400 border-violet-600/30",
  validator_approved: "bg-emerald-600/20 text-emerald-400 border-emerald-600/30",
  listed: "bg-emerald-600 text-white border-emerald-700",
  sold: "bg-stone-700 text-stone-300 border-stone-600",
  rejected: "bg-red-600/20 text-red-400 border-red-600/30",
  legacy: "bg-stone-700 text-stone-400 border-stone-600",
};

export default function RegistryMonitor() {
  const [projects, setProjects] = useState([]);
  const [counts, setCounts] = useState({});
  const [filter, setFilter] = useState("pending_admin");
  const [selected, setSelected] = useState(null);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState("");

  const fetchProjects = useCallback(async () => {
    setLoading(true);
    try {
      const params = filter === "all" ? {} : { status: filter };
      const r = await axios.get(`${API}/admin/hub/listings`, {
        withCredentials: true, params,
      });
      setProjects(r.data?.projects || []);
      setCounts(r.data?.counts || {});
    } catch (e) {
      setMessage(`Load failed: ${e.message}`);
    } finally {
      setLoading(false);
    }
  }, [filter]);

  useEffect(() => { fetchProjects(); }, [fetchProjects]);

  const refreshSelected = async (id) => {
    try {
      const r = await axios.get(`${API}/hub/listings/${id}`);
      setSelected(r.data);
      fetchProjects();
    } catch { /* silent */ }
  };

  const adminApprove = async (id) => {
    setMessage("");
    try {
      await axios.post(`${API}/admin/hub/${id}/admin-approve`, {}, { withCredentials: true });
      setMessage("Admin approval recorded.");
      refreshSelected(id);
    } catch (e) {
      setMessage(e.response?.data?.detail || "Approve failed");
    }
  };

  const reject = async (id) => {
    const reason = window.prompt("Rejection reason:") || "Rejected by admin";
    try {
      await axios.post(`${API}/admin/hub/${id}/reject`, { reason }, { withCredentials: true });
      setMessage("Project rejected.");
      refreshSelected(id);
    } catch (e) {
      setMessage(e.response?.data?.detail || "Reject failed");
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Permanently delete this project? This cannot be undone.")) return;
    try {
      await axios.delete(`${API}/admin/hub/${id}`, { withCredentials: true });
      setMessage("Project deleted.");
      setSelected(null);
      fetchProjects();
    } catch (e) {
      setMessage(e.response?.data?.detail || "Delete failed");
    }
  };

  const assignValidator = async (id) => {
    const name = window.prompt("External validator name:");
    if (!name) return;
    const email = window.prompt("External validator email:");
    if (!email) return;
    try {
      await axios.post(`${API}/admin/hub/${id}/assign-validator`,
        { validator_name: name, validator_email: email },
        { withCredentials: true });
      setMessage(`Validator ${name} assigned.`);
      refreshSelected(id);
    } catch (e) {
      setMessage(e.response?.data?.detail || "Assignment failed");
    }
  };

  const uploadValidatorReport = async (id, file, decision, notes) => {
    if (!file) return;
    const fd = new FormData();
    fd.append("report", file);
    fd.append("decision", decision);
    fd.append("notes", notes || "");
    try {
      await axios.post(`${API}/admin/hub/${id}/validator-upload`, fd, {
        withCredentials: true,
      });
      setMessage(`Validator report uploaded. Status: ${decision}.`);
      refreshSelected(id);
    } catch (e) {
      setMessage(e.response?.data?.detail || "Upload failed");
    }
  };

  return (
    <div className="space-y-4" data-testid="registry-monitor">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h2 className="font-heading text-xl font-semibold mb-1">Registry & Marketplace Monitor</h2>
          <p className="text-xs text-stone-500 font-body">
            Unified workflow for carbon project submissions. Approve, assign validators, upload validator reports, reject, or delete.
          </p>
        </div>
        <button
          onClick={fetchProjects}
          className="text-xs text-stone-400 hover:text-white flex items-center gap-1 px-2 py-1"
          data-testid="registry-refresh-btn"
        >
          <ArrowsClockwise size={12} /> Refresh
        </button>
      </div>

      {message && (
        <div className="bg-emerald-600/10 border border-emerald-600/30 text-emerald-300 text-xs px-3 py-2 rounded-lg" data-testid="registry-message">
          {message}
        </div>
      )}

      <div className="flex flex-wrap gap-2" data-testid="registry-filter-bar">
        {STATUS_OPTIONS.map((s) => {
          const count = s.id === "all"
            ? Object.values(counts).reduce((a, b) => a + b, 0)
            : counts[s.id] || 0;
          const active = filter === s.id;
          return (
            <button
              key={s.id}
              onClick={() => setFilter(s.id)}
              className={`text-xs px-3 py-1 rounded-full border transition-colors ${
                active
                  ? "bg-emerald-600 border-emerald-700 text-white"
                  : "bg-stone-900 border-stone-700 text-stone-400 hover:text-white"
              }`}
              data-testid={`registry-filter-${s.id}`}
            >
              {s.label} ({count})
            </button>
          );
        })}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Projects list */}
        <div className="lg:col-span-1 bg-stone-900 rounded-xl border border-stone-800 max-h-[70vh] overflow-y-auto" data-testid="registry-list">
          {loading ? (
            <div className="text-center py-10 text-stone-500 text-sm">Loading...</div>
          ) : projects.length === 0 ? (
            <div className="text-center py-10 text-stone-500 text-sm">No projects in this view.</div>
          ) : (
            projects.map((p) => (
              <button
                key={p.id}
                onClick={() => setSelected(p)}
                className={`w-full text-left px-4 py-3 border-b border-stone-800/50 hover:bg-stone-800/40 ${
                  selected?.id === p.id ? "bg-stone-800/60" : ""
                }`}
                data-testid={`registry-list-item-${p.id}`}
              >
                <div className="flex items-start justify-between gap-2 mb-1">
                  <p className="text-sm text-white truncate flex-1">
                    {p.location_name || p.project_type}
                  </p>
                  <span className={`text-[9px] uppercase px-1.5 py-0.5 rounded border whitespace-nowrap ${STATUS_COLOR[p.status]}`}>
                    {STAGE_LABEL[p.status] || p.status}
                  </span>
                </div>
                <p className="text-xs text-stone-500 truncate">{p.seller_name} · {p.area_ha} ha</p>
                <p className="text-[10px] text-stone-600 mt-0.5">{p.estimated_credits || 0} tCO₂e · ${p.total_price || 0}</p>
              </button>
            ))
          )}
        </div>

        {/* Detail panel */}
        <div className="lg:col-span-2">
          {selected ? (
            <ProjectDetail
              project={selected}
              onAdminApprove={() => adminApprove(selected.id)}
              onAssignValidator={() => assignValidator(selected.id)}
              onUploadReport={(file, decision, notes) => uploadValidatorReport(selected.id, file, decision, notes)}
              onReject={() => reject(selected.id)}
              onDelete={() => remove(selected.id)}
            />
          ) : (
            <div className="bg-stone-900/50 rounded-xl border border-dashed border-stone-700 p-10 text-center text-stone-500 text-sm">
              Select a project from the list to manage it.
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function ProjectDetail({ project, onAdminApprove, onAssignValidator, onUploadReport, onReject, onDelete }) {
  const [reportFile, setReportFile] = useState(null);
  const [reportDecision, setReportDecision] = useState("approved");
  const [reportNotes, setReportNotes] = useState("");
  const history = project.approval_history || [];

  return (
    <div className="bg-stone-900 rounded-xl border border-stone-800 p-5 space-y-5" data-testid={`registry-detail-${project.id}`}>
      <div className="flex items-start gap-3">
        <div className="w-12 h-12 rounded-xl bg-emerald-600/20 flex items-center justify-center flex-shrink-0">
          <Storefront size={20} weight="duotone" className="text-emerald-400" />
        </div>
        <div className="flex-1">
          <h3 className="font-heading text-base font-semibold text-white">
            {project.location_name || project.project_type}
          </h3>
          <p className="text-xs text-stone-500 flex items-center gap-1 mt-0.5">
            <MapPin size={11} /> {project.lat?.toFixed(4)}, {project.lon?.toFixed(4)} · {project.seller_name} ({project.seller_email || "—"})
          </p>
        </div>
        <span className={`text-[10px] uppercase px-2 py-1 rounded-full border whitespace-nowrap ${STATUS_COLOR[project.status]}`}>
          {STAGE_LABEL[project.status]}
        </span>
      </div>

      {/* Fact grid */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-xs">
        <Stat label="Area" value={`${project.area_ha} ha`} />
        <Stat label="Type" value={project.project_type} />
        <Stat label="Credits" value={`${project.estimated_credits || 0} tCO₂e`} />
        <Stat label="Total" value={`$${project.total_price || 0}`} />
      </div>

      {project.description && (
        <p className="text-xs text-stone-400 leading-relaxed">{project.description}</p>
      )}

      {/* Documents */}
      <div className="space-y-2">
        <p className="text-[11px] uppercase tracking-wider text-stone-500">Documents</p>
        {project.council_letter_path ? (
          <FileRow id={project.id} kind="council_letter" label="Council Letter" testId="reg-doc-council" />
        ) : (
          <p className="text-xs text-stone-600 italic">No council letter</p>
        )}
        {project.proof_photo_path && (
          <FileRow id={project.id} kind="proof_photo" label="Proof Photo" testId="reg-doc-photo" />
        )}
        {project.validator_report_path && (
          <FileRow id={project.id} kind="validator_report" label={`Validator Report — ${project.validator_name || ""}`} testId="reg-doc-validator" />
        )}
      </div>

      {/* Approval history */}
      {history.length > 0 && (
        <div className="space-y-2">
          <p className="text-[11px] uppercase tracking-wider text-stone-500">Approval History</p>
          <ol className="space-y-1.5 text-xs">
            {history.map((h, i) => (
              <li key={i} className="flex gap-2 text-stone-400">
                <span className="text-stone-600 w-4 flex-shrink-0">{i + 1}.</span>
                <div>
                  <span className="text-stone-300">{h.stage}</span> by {h.by_name} ·{" "}
                  <span className="text-stone-600">{new Date(h.at).toLocaleString()}</span>
                  {h.notes && <p className="italic text-stone-500">{h.notes}</p>}
                </div>
              </li>
            ))}
          </ol>
        </div>
      )}

      {/* Actions */}
      <div className="border-t border-stone-800 pt-4 space-y-3">
        <p className="text-[11px] uppercase tracking-wider text-stone-500">Actions</p>

        <div className="flex flex-wrap gap-2">
          {project.status === "pending_admin" && (
            <button onClick={onAdminApprove}
              className="bg-sky-600 hover:bg-sky-700 text-white text-xs px-3 py-1.5 rounded-lg flex items-center gap-1.5"
              data-testid="reg-admin-approve-btn">
              <ShieldCheck size={12} weight="bold" /> Admin Approve
            </button>
          )}
          {(project.status === "admin_approved" || project.status === "pending_validator") && (
            <button onClick={onAssignValidator}
              className="bg-violet-600 hover:bg-violet-700 text-white text-xs px-3 py-1.5 rounded-lg flex items-center gap-1.5"
              data-testid="reg-assign-validator-btn">
              <UserPlus size={12} weight="bold" /> {project.validator_name ? "Reassign" : "Assign"} Validator
            </button>
          )}
          <button onClick={onReject}
            className="bg-red-600/20 hover:bg-red-600/30 text-red-400 text-xs px-3 py-1.5 rounded-lg flex items-center gap-1.5"
            data-testid="reg-reject-btn">
            <XCircle size={12} weight="bold" /> Reject
          </button>
          <button onClick={onDelete}
            className="bg-stone-800 hover:bg-stone-700 text-stone-400 text-xs px-3 py-1.5 rounded-lg flex items-center gap-1.5 ml-auto"
            data-testid="reg-delete-btn">
            <Trash size={12} weight="bold" /> Delete
          </button>
        </div>

        {/* Validator report upload */}
        {(project.status === "admin_approved" || project.status === "pending_validator") && (
          <div className="bg-stone-800/40 border border-stone-700 rounded-lg p-3 space-y-2">
            <p className="text-xs text-stone-300 font-medium">Upload Validator Report</p>
            <label className="flex items-center gap-2 text-xs text-stone-400 cursor-pointer">
              <FileArrowUp size={14} />
              <span className="flex-1 truncate">{reportFile?.name || "Choose PDF/JPG/PNG"}</span>
              <input type="file" accept=".pdf,image/*"
                onChange={(e) => setReportFile(e.target.files?.[0] || null)}
                className="hidden" data-testid="reg-validator-file-input" />
            </label>
            <div className="flex gap-2">
              <select value={reportDecision} onChange={(e) => setReportDecision(e.target.value)}
                className="bg-stone-900 border border-stone-700 rounded-lg px-2 py-1 text-xs text-white"
                data-testid="reg-validator-decision">
                <option value="approved">Approved → List</option>
                <option value="rejected">Rejected</option>
              </select>
              <input type="text" placeholder="Notes (optional)" value={reportNotes}
                onChange={(e) => setReportNotes(e.target.value)}
                className="flex-1 bg-stone-900 border border-stone-700 rounded-lg px-2 py-1 text-xs text-white"
                data-testid="reg-validator-notes" />
              <button
                onClick={() => onUploadReport(reportFile, reportDecision, reportNotes)}
                disabled={!reportFile}
                className="bg-emerald-600 hover:bg-emerald-700 disabled:bg-stone-700 text-white text-xs px-3 py-1 rounded-lg"
                data-testid="reg-validator-upload-btn">
                Upload
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function Stat({ label, value }) {
  return (
    <div className="bg-stone-800/40 rounded-lg p-2">
      <p className="text-[10px] uppercase text-stone-500">{label}</p>
      <p className="text-sm text-white">{value}</p>
    </div>
  );
}

function FileRow({ id, kind, label, testId }) {
  return (
    <a
      href={`${API}/hub/listings/${id}/file/${kind}`}
      target="_blank"
      rel="noopener noreferrer"
      className="flex items-center gap-2 bg-stone-800/40 hover:bg-stone-800 rounded-lg px-3 py-2 text-xs text-stone-300"
      data-testid={testId}
    >
      <DownloadSimple size={12} className="text-emerald-400" />
      <span className="flex-1 truncate">{label}</span>
    </a>
  );
}
